import { buildCollection, buildProperty } from "@firecms/core";
import { ManageSearchIcon } from "@firecms/ui";
import { createElement } from "react";

import { ExportCollectionAction } from "@/actions/export_collection";
import { ModMedResourcesView } from "@/custom/ModMedResourcesView";
import { TimezoneField } from "@/custom/TimezoneField";

type NexhealthCustomer = {
  name: string;
  environment: "production" | "sandbox";
  institution_id?: string;
  location_id?: string;
  subdomain?: string;
  subscribed_to_webhook: boolean;
  subscribed_to_webhook_timestamp?: Date;
};

const pmsEnumValues = {
  abeldent: "AbelDent",
  advantage: "Advantage",
  athenahealth: "athenahealth",
  autopia: "Autopia",
  avimark: "AVIMark",
  axxium_visionr: "Axxium VisionR",
  cerbo: "Cerbo",
  chirotouch: "ChiroTouch",
  cleardent: "ClearDent",
  clienttrax: "ClientTrax",
  cloud9: "Cloud9",
  cornerstone: "CornerStone",
  curve_hero: "Curve Hero",
  dentalvision: "DentalVision",
  dentech: "Dentech",
  denticon: "Denticon",
  dentimax: "DentiMax",
  dentitek: "DentiTek",
  dentonovo: "Dentonovo",
  dentrix: "Dentrix",
  dentrix_ascend: "Dentrix Ascend",
  dentrix_enterprise: "Dentrix Enterprise",
  dolphin: "Dolphin",
  dom: "DOM",
  domtrak: "Domtrak",
  dox_pedo: "Dox Pedo",
  drchrono: "DrChrono",
  dsn_dental: "DSN-Dental",
  dsn_oms: "DSN-OMS",
  dsn_perio: "DSN-Perio",
  dvmax: "DVMAX",
  eaglesoft: "Eaglesoft",
  easy_dental: "Easy Dental",
  eclinicalworks: "eClinicalWorks",
  eclipse: "Eclipse",
  elation: "Elation",
  endovision: "EndoVision",
  exceldent: "Exceldent",
  genesis: "Genesis",
  gohighlevel: "GoHighLevel",
  gold: "Gold",
  healthie: "Healthie",
  hvms: "HVMS",
  impromed_infinity: "ImpromedInfinity",
  intakeq_practiceq: "IntakeQ / PracticeQ",
  intravet: "Intravet",
  kareo: "Kareo",
  liveddm: "LiveDDM",
  maxident: "Maxident",
  mediadent: "MediaDent",
  medinformatix: "Medinformatix",
  medisoft: "MediSoft",
  modmed: "ModMed",
  mogo: "Mogo",
  nextgen_office: "NextGen Office",
  ocs_office_partner: "OCS-Office Partner",
  officemate: "OfficeMate",
  omsvision: "OMSVision",
  opendental: "Open Dental",
  openvpms: "OpenVPMS",
  opes: "Opes",
  ortho2_viewpoint: "Ortho2 ViewPoint",
  ortho2edge: "Ortho2Edge",
  orthotrac: "OrthoTrac",
  oryx: "Oryx",
  paradigm: "Paradigm",
  pbs_endo: "PBS Endo",
  periovision: "PerioVision",
  planetdds: "PlanetDDS",
  power_practice_pro: "Power Practice Pro",
  practice_better: "Practice Better",
  practice_ehr: "Practice EHR",
  practiceweb: "PracticeWeb",
  practiceworks: "PracticeWorks",
  private_practice_software: "Private Practice Software",
  progident: "Progident",
  qsidental_web: "QSIDental Web",
  rxworks: "RxWorks",
  shelter_buddy: "Shelter Buddy",
  softdent: "SoftDent",
  software_of_excellence: "Software of Excellence",
  stringsoft: "StringSoft",
  suzydental: "SuzyDental",
  tebra: "Tebra",
  total_dental: "Total Dental",
  tracker: "Tracker",
  vetech: "Vetech",
  vetspace: "VetSpace",
  via: "VIA",
  windent: "WinDent",
  windentsql: "WinDentSQL",
  winoms: "WinOMS",
  xldent: "XLDent",
  zenoti_emr: "Zenoti EMR",
  other: "Other",
};

const practiceProperty = buildProperty({
  name: "Practice",
  dataType: "map",
  properties: {
    service: buildProperty({
      name: "Service",
      dataType: "string",
      enumValues: {
        gohighlevel: "GoHighLevel",
        sikka: "Sikka",
        nexhealth: "NexHealth",
        redox: "Redox",
        modmed: "ModMed",
        dashone: "DashOne",
        custom: "Custom",
      },
    }),
    pms: buildProperty({
      name: "PMS",
      dataType: "string",
      validation: {
        required: false,
      },
      enumValues: pmsEnumValues,
    }),
    id: buildProperty({
      name: "Id",
      dataType: "string",
    }),
  },
});

export type Timezone = {
  label: string; // e.g. "(UTC-05:00) America/New_York"
  tzCode: string; // e.g. "America/New_York"
  name: string; // e.g. "Eastern Standard Time (UTC-05:00)"
  utc: string; // e.g. "UTC-05:00"
};

export const appointmentConnectionsCollections = buildCollection({
  id: "connections",
  name: "Appointment Connections",
  group: "Appointment Manager",
  textSearchEnabled: true,
  path: "connections",
  Actions: ExportCollectionAction,
  properties: {
    name: buildProperty({
      name: "Name",
      validation: { required: true },
      dataType: "string",
    }),
    active: buildProperty({
      name: "Active",
      validation: { required: false },
      defaultValue: true,
      dataType: "boolean",
    }),
    origin: { ...practiceProperty, name: "Origin" },
    target: { ...practiceProperty, name: "Target" },
    appointmentGrouping: buildProperty({
      name: "Appointment Grouping",
      description:
        "Determines how appointments are organized - by individual provider or by practice location",
      longDescription:
        "When using By Provider, appointments will be grouped by individual providers. Each provider will have it's own calendar. When using By Location, appointments will be grouped by the practice location.",
      defaultValue: "provider",
      dataType: "string",
      enumValues: {
        provider: "By Provider",
        location: "By Location",
      },
      validation: { required: false },
    }),
    image: buildProperty({
      name: "Practice Image",
      description: "This image will be used in the booking app",
      dataType: "string",
      storage: {
        storagePath: "images",
        acceptedFiles: ["image/*"],
      },
      validation: { required: false },
    }),
    bidirectional: buildProperty({
      name: "Bidirectional",
      dataType: "boolean",
    }),
    timezone: buildProperty<Timezone>({
      name: "Timezone",
      dataType: "map",
      previewProperties: ["tzCode"],
      validation: { required: false },
      defaultValue: null,
      properties: {
        label: buildProperty({
          name: "Label",
          validation: { required: true },
          dataType: "string",
          description: "e.g. (UTC-05:00) America/New_York",
        }),
        tzCode: buildProperty({
          name: "Timezone Code",
          validation: { required: true },
          dataType: "string",
          description: "e.g. America/New_York",
        }),
        name: buildProperty({
          name: "Name",
          validation: { required: true },
          dataType: "string",
          description: "e.g. Eastern Standard Time (UTC-05:00)",
        }),
        utc: buildProperty({
          name: "UTC Offset",
          validation: { required: true },
          dataType: "string",
          description: "e.g. UTC-05:00",
        }),
      },
      Field: TimezoneField,
    }),
    defaultAppointmentStatus: buildProperty({
      name: "Default Appointment Status",
      dataType: "string",
      defaultValue: "confirmed",
      enumValues: {
        confirmed: "Confirmed",
        new: "New (Pending)",
      },
      validation: { required: false },
    }),
    slotConfig: buildProperty({
      name: "Appointment Slot Configuration",
      description: "Duration and interval of the slots",
      defaultValue: { duration: 5, interval: 5 },
      validation: { required: false },
      spreadChildren: false,
      dataType: "map",
      properties: {
        interval: buildProperty({
          name: "Appointment Interval",
          placeholder: "Interval in minutes",
          dataType: "number",
          defaultValue: 5,
          validation: { required: true },
        }),
        duration: buildProperty({
          name: "Appointment Duration",
          placeholder: "Duration in minutes",
          dataType: "number",
          defaultValue: 15,
          validation: { required: true },
        }),
        days: buildProperty(({ values }) => ({
          name: "Days To Show",
          description: "Maximum number of days to show in the booking app",
          dataType: "number",
          defaultValue: 5,
          validation: {
            min: 1,
            max: values.origin?.service === "modmed" ? 30 : undefined,
          },
        })),
      },
    }),
    pollingConfig: buildProperty(({ values }) => ({
      name: "Polling Configuration",
      description: "How far ahead the polling service retrieves appointments",
      longDescription:
        "Only used by Sikka connections. The polling service runs once a day and retrieves " +
        "appointments from yesterday up to this many days ahead of today. Raising it syncs " +
        "appointments further into the future, at the cost of a proportionally larger volume " +
        "of appointments processed on every run.",
      dataType: "map",
      validation: { required: false },
      spreadChildren: false,
      disabled:
        values.origin?.service === "sikka"
          ? undefined
          : {
              hidden: true,
              disabledMessage: "Polling configuration only applies to Sikka connections",
            },
      properties: {
        daysAhead: buildProperty({
          name: "Days Ahead",
          description: "Number of days ahead of today to retrieve appointments for. Defaults to 1.",
          dataType: "number",
          defaultValue: 1,
          validation: { required: false, min: 1, max: 10, integer: true },
        }),
      },
    })),
    displayAppointmentTypeFirst: buildProperty({
      name: "Display Appointment Type First",
      description: "Display appointment type first in the booking app",
      validation: { required: false },
      defaultValue: false,
      dataType: "boolean",
    }),
    skipProviderSelection: buildProperty({
      name: "Skip Provider Selection",
      description:
        "Skip provider selection step in the booking app if only one provider is available",
      validation: { required: false },
      defaultValue: false,
      dataType: "boolean",
    }),
    minimumDaysBeforeBooking: buildProperty({
      name: "Minimum Days Before Booking",
      description: "Number of days in advance a patient can book an appointment",
      validation: { required: false },
      dataType: "number",
      defaultValue: 1,
    }),
    allowedStartTimes: buildProperty({
      name: "Allowed Start Times",
      description:
        "Only offer slots starting at these times, in the location's time zone, as HH:mm in " +
        "24-hour format (e.g. 09:30, 22:00). Leave empty to offer every free slot.",
      validation: { required: false },
      dataType: "array",
      of: {
        dataType: "string",
        validation: {
          required: true,
          trim: true,
          matches: /^([01]\d|2[0-3]):[0-5]\d$/,
          matchesMessage: "Use HH:mm in 24-hour format, e.g. 09:30 or 22:00",
        },
      },
    }),
    providersWhitelist: buildProperty({
      name: "Providers Whitelist",
      description: "List of providers that are allowed to book appointments",
      validation: { required: false },
      dataType: "array",
      of: {
        dataType: "map",
        properties: {
          id: buildProperty({
            name: "ID",
            dataType: "string",
            validation: { trim: true },
          }),
          name: buildProperty({
            name: "Name",
            dataType: "string",
            validation: { trim: true },
          }),
          alias: buildProperty({
            name: "Alias",
            description: "An optional display name for the provider in the booking app",
            dataType: "string",
            validation: { trim: true },
          }),
          appointmentTypesWhitelist: buildProperty({
            name: "Appointment Types Whitelist",
            description: "List of appointment types allowed",
            validation: { required: false },
            dataType: "array",
            of: {
              dataType: "map",
              properties: {
                id: buildProperty({
                  name: "ID",
                  dataType: "string",
                  validation: { trim: true },
                }),
                name: buildProperty({
                  name: "Name",
                  dataType: "string",
                  validation: { trim: true },
                }),
              },
            },
          }),
          image: buildProperty({
            name: "Provider profile image",
            description: "This image will be shown in the booking app",
            dataType: "string",
            storage: {
              storagePath: "images",
              acceptedFiles: ["image/*"],
            },
            validation: { required: false },
          }),
        },
      },
    }),
    appointmentTypesWhitelist: buildProperty({
      name: "Appointment Types Whitelist",
      description: "List of appointment types allowed",
      validation: { required: false },
      dataType: "array",
      of: {
        dataType: "map",
        properties: {
          id: buildProperty({
            name: "ID",
            dataType: "string",
            validation: { trim: true },
          }),
          name: buildProperty({
            name: "Name",
            dataType: "string",
            validation: { trim: true },
          }),
        },
      },
    }),
    appointmentTypesBlacklist: buildProperty({
      name: "Appointment Types Blacklist",
      description: "List of appointment types to filter out.",
      longDescription: "These appointments will not be processed.",
      validation: { required: false },
      dataType: "array",
      of: {
        dataType: "map",
        properties: {
          name: buildProperty({
            name: "Name",
            dataType: "string",
            validation: { trim: true },
          }),
        },
      },
    }),
    extraFields: buildProperty({
      name: "Extra Fields",
      description: "Additional fields to collect from patients during the booking process",
      validation: { required: false },
      dataType: "array",
      of: {
        dataType: "string",
        enumValues: {
          date_of_birth: {
            id: "date_of_birth",
            label: "Date of Birth",
            color: "blueDark",
          },
        },
      },
    }),
  },
});

export const nexHealthCustomersCollection = buildCollection<NexhealthCustomer>({
  id: "nexhealth",
  name: "Nexhealth Customers",
  singularName: "Customer",
  group: "Partners",
  textSearchEnabled: true,
  path: "nexhealth",
  properties: {
    name: buildProperty({
      name: "Name",
      validation: { required: true },
      dataType: "string",
    }),
    environment: buildProperty({
      name: "Environment",
      validation: { required: true },
      dataType: "string",
      enumValues: {
        production: "Production",
        sandbox: "Sandbox",
      },
    }),
    institution_id: buildProperty({
      name: "Institution Id",
      validation: { required: false },
      dataType: "string",
    }),
    location_id: buildProperty({
      name: "Location Id",
      validation: { required: true },
      dataType: "string",
    }),
    subdomain: buildProperty({
      name: "Subdomain",
      validation: { required: true },
      dataType: "string",
    }),
    subscribed_to_webhook: buildProperty({
      name: "Subscribed to webhook endpoint",
      readOnly: true,
      description: "Read only, value set automatically",
      validation: { required: false },
      dataType: "boolean",
    }),
    subscribed_to_webhook_timestamp: buildProperty({
      name: "Subscribed to webhook endpoint timestamp",
      validation: { required: false },
      description: "Read only, value set automatically",
      readOnly: true,
      dataType: "date",
    }),
  },
});

type ModMedCustomer = {
  name: string;
  environment: "production" | "sandbox" | "development";
  firmPrefix: string;
  locationId: string;
  username: string;
  password: string;
  apiKey: string;
};

export const modmedCustomersCollection = buildCollection<ModMedCustomer>({
  id: "modmed",
  name: "ModMed Customers",
  singularName: "Customer",
  group: "Partners",
  textSearchEnabled: true,
  path: "modmed",
  entityViews: [
    {
      key: "resources",
      name: "Resources",
      Builder: ModMedResourcesView,
    },
  ],
  entityActions: [
    {
      name: "Resources",
      key: "resources",
      icon: createElement(ManageSearchIcon),
      collapsed: false,
      includeInForm: false,
      onClick: ({ entity, context }) => {
        context.sideEntityController.open({
          path: entity.path,
          entityId: entity.id,
          selectedTab: "resources",
          updateUrl: true,
        });
      },
    },
  ],
  properties: {
    name: buildProperty({
      name: "Name",
      validation: { required: true },
      dataType: "string",
    }),
    environment: buildProperty({
      name: "Environment",
      validation: { required: true },
      dataType: "string",
      enumValues: {
        production: "Production",
        sandbox: "Sandbox",
        development: "Development",
      },
    }),
    firmPrefix: buildProperty({
      name: "Firm Prefix",
      validation: { required: true },
      dataType: "string",
    }),
    locationId: buildProperty({
      name: "Location ID",
      validation: { required: false },
      dataType: "string",
    }),
    username: buildProperty({
      name: "Username",
      validation: { required: true },
      dataType: "string",
    }),
    password: buildProperty({
      name: "Password",
      validation: { required: true },
      dataType: "string",
    }),
    apiKey: buildProperty({
      name: "API Key",
      validation: { required: false },
      dataType: "string",
    }),
  },
});
